import {
  OrderStatus,
  TradeActionType,
  TradeData,
  TradeSourceType,
} from "@shared/types";

export interface OrderData<T = number> {
  price: T;
  percent: T;
  amount?: T;
  distance?: T;
}

export interface ValidationOrderData extends OrderData {
  status?: OrderStatus;
}

export interface CreateTradeOrder extends Pick<OrderData, "price" | "amount"> {
  ratio: number;
}

export enum OrderTypes {
  ENTRY = "entry",
  TAKE_PROFIT = "takeProfit",
  STOP = "stop",
}

export interface OrdersData<T = number> {
  [OrderTypes.ENTRY]: OrderData<T>[];
  [OrderTypes.TAKE_PROFIT]: OrderData<T>[];
  [OrderTypes.STOP]: OrderData<T>[];
}

export interface CreateTradeOrdersData {
  [OrderTypes.ENTRY]: CreateTradeOrder[];
  [OrderTypes.TAKE_PROFIT]: CreateTradeOrder[];
  [OrderTypes.STOP]: CreateTradeOrder[];
}

export interface ValidationOrdersData {
  [OrderTypes.ENTRY]: ValidationOrderData[];
  [OrderTypes.TAKE_PROFIT]: ValidationOrderData[];
  [OrderTypes.STOP]: ValidationOrderData[];
}

export type InputOrderData = OrderData<string>;
export type InputOrdersData = OrdersData<string>;
export type UpdateOrder = (
  index: number,
  orderType: OrderTypes,
  updatedOrder: InputOrderData
) => void;
export type RemoveOrder = (
  orderType: OrderTypes,
  indexToRemove: number
) => void;
export type SetOrders = (orders: InputOrderData[]) => void;

export enum ErrorTypes {
  DUPLICATE_PRICE,
  LONG_ENTRY_ABOVE_TAKE_PROFIT,
  SHORT_ENTRY_BELOW_TAKE_PROFIT,
  LONG_TAKE_PROFIT_BELOW_ENTRIES,
  SHORT_TAKE_PROFIT_ABOVE_ENTRIES,
  LONG_STOP_ABOVE_ENTRIES,
  SHORT_STOP_BELOW_ENTRIES,
  LONG_STOP_ABOVE_CURRENT_PRICE,
  SHORT_STOP_BELOW_CURRENT_PRICE,
  LONG_BREAKOUT_STOP_ABOVE_CURRENT_PRICE,
  SHORT_BREAKOUT_STOP_BELOW_CURRENT_PRICE,
  LONG_BREAKOUT_ENTRY_BELOW_CURRENT_PRICE,
  SHORT_BREAKOUT_ENTRY_ABOVE_CURRENT_PRICE,
  BREAKOUT_TOO_MANY_ENTRIES,
  SUM_OF_RATIOS,
  INVALID_ORDER_RATIO,
  INVALID_ENTRY_ORDER_AMOUNT,
  INVALID_TRADE_AMOUNT,
  INVALID_TRADE_QUANTITY,
  MAX_NUM_TRADES_EXCEEDED,
  INVALID_ORDER_PRICE,
  INVALID_LEVERAGE_MARGIN_TYPE,
  INVALID_LEVERAGE_MULTIPLIER,
  EDIT_TRADE_SUM_OF_RATIOS,
  AT_LEAST_ONE_ENTRY,
  INVALID_NUMBER_OF_TAKE_PROFIT_AND_STOP,
  RATIO_MUST_BE_SPECIFIED,
  CANNOT_BREAK_EVEN_UNFULFILLED_TARGETS,
  LONG_TAKE_PROFIT_BELOW_STOP,
  SHORT_TAKE_PROFIT_ABOVE_STOP,
  CANT_REDISTRIBUTE_NO_SIBLINGS_TARGETS,
  CANT_REDISTRIBUTE_NO_SIBLINGS_NON_FULFILLED_TARGETS,
  LONG_TAKE_PROFIT_BELOW_CURRENT_PRICE,
  SHORT_TAKE_PROFIT_ABOVE_CURRENT_PRICE,
}

export type ErrorTypeToDisplay = {
  [errorType in ErrorTypes]?: string;
};

export interface OrderSectionErrors {
  section: ErrorTypeToDisplay;
  orders: { [key: number]: ErrorTypeToDisplay };
}

export interface OrdersErrors {
  [OrderTypes.ENTRY]: OrderSectionErrors;
  [OrderTypes.TAKE_PROFIT]: OrderSectionErrors;
  [OrderTypes.STOP]: OrderSectionErrors;
}

export enum BulkCloseTradesStep {
  EXISTING_COINS,
  CONFIRM_INACTIVE_ACCOUNT,
  CONFIRM,
}

export interface BulkCloseTradesModalProps {
  open?: boolean;
  tradeIDs?: number[];
  closeModal: () => void;
  tradeIDToTradeData?: Record<number, TradeData>;
  onFinish?: () => void;
}

export enum TradeSource {
  AUTO_TRADING = 1,
  PERSONAL = 2,
  QUICK = 3,
  TRADINGVIEW_BOT = 4,
  MANUAL_FREETEXT = 5,
  MANUAL_GUIDED = 6,
  MANUAL_CREATE_ORDER = 7,
  SEMI_AUTO_TRADING_FOLLOW_SIGNAL = 8,
  SEMI_AUTO_TRADING_ONE_CLICK = 9,
  CLOSED_OPPOSITE_POSITION = 10,
  ASAP_BOT = 11,
  MANUAL_API = 12,
  GRID_BOT = 13,
  SIGNAL_BOT = 14,
}

export type HandleTradeActionClick = (
  actionType: TradeActionType,
  tradeData?: TradeData,
  action?: () => void,
  openShareModalDelay?: number
) => void;

export type OpenShareResultsModalType = (
  tradeId: number,
  symbol?: string,
  tradeSource?: TradeSourceType | TradeSource,
  clientIsInvalid?: boolean,
  onSuccess?: () => void,
  openModalDelay?: number
) => void;

export enum TradesTab {
  OPEN = "open",
  CLOSED = "closed",
}
